/**
 * The live decision of an email, the same rule as the back (contract v10.24, ADR 0020 of
 * `Cubas_Back`): the newest decision a person answered and, while nobody has answered any,
 * the newest. What the email **is** comes from it: the person's category when they
 * corrected it, the proposal otherwise.
 *
 * The back sends the result as `live_category`, `live_destination` and `reviewed`. The
 * screen needs the rule too, for one moment: after «Correcto» the detail is patched from
 * the decision the endpoint answers, not read again, and those three fields have to follow
 * the patch or the inbox would call a reviewed email «sin revisar» until the next refetch.
 */
import type { EmailDetail, TriageDecision } from './types';

/** Oldest first, `id` breaking a tie of `created_at`: the order the back writes in. */
export function sortDecisions(decisions: readonly TriageDecision[] | null | undefined) {
  return [...(decisions ?? [])].sort((a, b) =>
    a.created_at === b.created_at
      ? a.id.localeCompare(b.id)
      : a.created_at.localeCompare(b.created_at),
  );
}

export function liveDecisionOf(
  decisions: readonly TriageDecision[] | null | undefined,
): TriageDecision | undefined {
  const sorted = sortDecisions(decisions);
  const answered = sorted.filter((decision) => Boolean(decision.final_destination));
  return (answered.length > 0 ? answered : sorted).at(-1);
}

/** The email with `live_category`, `live_destination` and `reviewed` recomputed. */
export function withLiveFields(email: EmailDetail): EmailDetail {
  const live = liveDecisionOf(email.triage_decisions);
  return {
    ...email,
    live_category: live ? (live.corrected_category ?? live.category) : null,
    live_destination: live ? (live.final_destination ?? live.proposed_destination) : null,
    reviewed: Boolean(live?.final_destination),
  };
}
